import { Component, inject, viewChild } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { NovaButton } from '@rds-nova/angular-button';
import { NovaIcon } from '@rds-nova/angular-icon';
import { MatMenuModule } from '@angular/material/menu';
import { Store } from '@ngrx/store';

import { AppState } from 'src/app/store';
import {
  selectParticipantFullName,
  selectIsSessionAccessible,
} from 'src/app/features/session/store/selectors/session.selectors';
import { selectIsAuthenticated } from 'src/app/features/msal-auth/store/selectors/msal-auth.selectors';
import { ScreenSizeService } from 'src/app/shared/services/screen-size.service';

import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { UserMenuComponent } from './user-menu/user-menu.component';
import { HelpMenuComponent } from './help-menu/help-menu.component';
import { ExitSigningModalComponent } from '../exit-signing-modal/exit-signing-modal.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, combineLatest } from 'rxjs';
import { TrackedButtonDirective } from 'src/app/core/analytics/directives/tracked-button.directive';

@Component({
  selector: 'app-header',
  imports: [
    TranslatePipe,
    LanguageSelectorComponent,
    UserMenuComponent,
    HelpMenuComponent,
    NovaButton,
    NovaIcon,
    MatMenuModule,
    ExitSigningModalComponent,
    TrackedButtonDirective,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  private readonly store = inject(Store<AppState>);
  protected screenSize = inject(ScreenSizeService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  exitModal = viewChild<ExitSigningModalComponent>('exitModal');

  readonly participantName = this.store.selectSignal(selectParticipantFullName);

  get participantInitial(): string {
    const name = this.participantName();
    return name ? name.charAt(0).toUpperCase() : '';
  }

  readonly isAuthenticated = this.store.selectSignal(selectIsAuthenticated);

  showExitSigning = toSignal(
    combineLatest([
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(() => this.getRouteData('showExitSigning') === true)
      ),
      this.store.select(selectIsSessionAccessible)
    ]).pipe(
      map(([routeHasExitSigning, isSessionAccessible]) => routeHasExitSigning && isSessionAccessible)
    ),
    { initialValue: false }
  );

  private getRouteData(key: string): boolean | undefined {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot.data[key];
  }

  exitSigning(): void {
    this.exitModal()?.open();
  }
}
